import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/SectionHeading";
import { STORE } from "@/constants/testIds";
import { CONTACT_EMAIL } from "@/data/content";

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Block = ({ title, children }) => (
  <section className="mt-7 border-t border-[color:rgba(10,27,51,0.10)] pt-7 first:mt-0 first:border-t-0 first:pt-0">
    <h2 className="font-[var(--font-heading)] text-lg font-semibold text-[var(--ibp-ink)]">
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
      {children}
    </div>
  </section>
);

export default function Terms() {
  return (
    <div data-testid={STORE.termsPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Legal"
            title="Terms of Service"
            subtitle="The terms that govern your use of Illinois Broker Prep."
          />
        </Container>
      </section>

      <section className="py-14 sm:py-20">
        <Container>
          <div className="mx-auto max-w-3xl rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-7 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-9">
            <p className="text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
              Last updated: June 2026.
            </p>
            <p className="mt-4 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
              These terms apply to Illinois Broker Prep, a study tool operated
              by Coursewright ("Coursewright," "we," "us," or "our").
            </p>

            <div className="mt-7">
              <Block title="Acceptance of these terms">
                <p>
                  By accessing or using Illinois Broker Prep, you agree to these
                  Terms of Service. If you do not agree, please do not use the
                  site or the online study tool.
                </p>
              </Block>

              <Block title="What this product is">
                <p>
                  Illinois Broker Prep is an independent study aid. It is not
                  affiliated with PSI, IDFPR, the State of Illinois, or any real
                  estate commission. It does not contain actual exam questions
                  and does not replace required pre-license education, official
                  exam bulletins, or legal guidance. We make no guarantee that
                  you will pass any exam.
                </p>
              </Block>

              <Block title="Purchases &amp; delivery">
                <p>
                  Purchases are processed by a secure third-party checkout
                  provider. Full access is connected to the email used at checkout,
                  and account instructions are emailed after payment. If automated
                  delivery is delayed, support completes activation within one business day. Refunds are
                  handled according to our{" "}
                  <Link to="/refund" className="font-semibold text-[#0F3D8A] hover:underline">
                    Refund Policy
                  </Link>
                  .
                </p>
              </Block>

              <Block title="Accounts &amp; personal access">
                <p>
                  Student accounts and paid access are for personal use only and
                  may not be shared, resold, or published. Sign-in codes are
                  temporary security credentials and should not be shared. We may
                  disable accounts that are misused. Existing customers with a
                  legacy access code may use it to move their saved progress into
                  the account system.
                </p>
              </Block>

              <Block title="Lifetime access">
                <p>
                  Lifetime access means your access stays active for the
                  operational life of the Illinois Broker Prep online study tool
                  — yours until you pass — unless the purchase is refunded or the account is shared,
                  resold, published, or otherwise misused.
                </p>
              </Block>

              <Block title="Acceptable use &amp; sharing policy">
                <p className="font-medium text-[var(--ibp-ink)]">
                  Your access is for personal study use only. You may not share,
                  resell, copy, upload, publish, scrape, reproduce, distribute, or
                  redistribute Illinois Broker Prep questions, explanations,
                  dashboards, or study materials. Accounts may be disabled for
                  suspected abuse, unauthorized sharing, or redistribution.
                </p>
                <ul className="list-disc space-y-1.5 pl-5">
                  <li>Do not copy, redistribute, resell, or publish our content.</li>
                  <li>Do not scrape, bulk-export, or attempt to reverse-engineer the service.</li>
                  <li>Use the materials only for your own exam preparation.</li>
                </ul>
              </Block>

              <Block title="Disclaimers &amp; limitation of liability">
                <p>
                  The service is provided as-is, without warranties of any
                  kind. To the fullest extent permitted by law, Illinois Broker
                  Prep is not liable for any indirect, incidental, or
                  consequential damages, or for exam outcomes, arising from your
                  use of the materials.
                </p>
              </Block>

              <Block title="Changes &amp; governing law">
                <p>
                  We may update these terms from time to time. Continued use
                  after changes means you accept the updated terms. These terms
                  are governed by the laws of the State of Illinois.
                </p>
              </Block>

              <Block title="Contact">
                <p>
                  Questions about these terms? Email{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-semibold text-[#0F3D8A] hover:underline"
                  >
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
              </Block>
            </div>

            <p className="mt-8 text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
              Illinois Broker Prep is an independent study aid. Not affiliated
              with PSI, IDFPR, the State of Illinois, or any real estate
              commission. No actual exam questions. No pass guarantee.
            </p>
          </div>
        </Container>
      </section>
    </div>
  );
}
